"use client"

import { useState } from "react"
import { Button } from "#/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "#/components/ui/avatar"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import { Badge } from "#/components/ui/badge"
import { Heart, MessageCircle, UserPlus, X } from "lucide-react"
import { formatDistanceToNow } from "date-fns"

interface Notification {
  id: string
  type: "like" | "comment" | "follow" | "mention"
  user: {
    id: string
    username: string
    displayName: string
    avatar?: string
  }
  content?: string
  postId?: string
  createdAt: Date
  isRead: boolean
}

interface NotificationsPanelProps {
  isOpen: boolean
  onClose: () => void
}

const mockNotifications: Notification[] = [
  {
    id: "1",
    type: "like",
    user: {
      id: "2",
      username: "maya_writes",
      displayName: "Maya Rodriguez",
      avatar: "/latina-writer-glasses.png",
    },
    postId: "1",
    createdAt: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
    isRead: false,
  },
  {
    id: "2",
    type: "comment",
    user: {
      id: "3",
      username: "david_travels",
      displayName: "David Kim",
      avatar: "/asian-man-backpack-portrait.png",
    },
    content: "Amazing shot! What camera did you use?",
    postId: "1",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    isRead: false,
  },
  {
    id: "3",
    type: "follow",
    user: {
      id: "4",
      username: "sarah_creates",
      displayName: "Sarah Johnson",
      avatar: "/blonde-artist-portrait.png",
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    isRead: true,
  },
]

export function NotificationsPanel({
  isOpen,
  onClose,
}: NotificationsPanelProps) {
  const [notifications, setNotifications] = useState(mockNotifications)

  const markAsRead = (notificationId: string) => {
    setNotifications(
      notifications.map(notif =>
        notif.id === notificationId ? { ...notif, isRead: true } : notif,
      ),
    )
  }

  const markAllAsRead = () => {
    setNotifications(notifications.map(notif => ({ ...notif, isRead: true })))
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "like":
        return <Heart className="h-4 w-4 text-red-500" />
      case "comment":
        return <MessageCircle className="h-4 w-4 text-blue-500" />
      case "follow":
        return <UserPlus className="h-4 w-4 text-green-500" />
      default:
        return null
    }
  }

  const getNotificationText = (notification: Notification) => {
    switch (notification.type) {
      case "like":
        return "liked your post"
      case "comment":
        return "commented on your post"
      case "follow":
        return "started following you"
      case "mention":
        return "mentioned you in a post"
      default:
        return ""
    }
  }

  if (!isOpen) return null

  const unreadCount = notifications.filter(n => !n.isRead).length

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-20">
      <Card className="w-full max-w-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">Notifications</CardTitle>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {unreadCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                  Mark all read
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Heart className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-3 p-4 hover:bg-muted/50 cursor-pointer ${
                    !notification.isRead ? "bg-primary/5" : ""
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={notification.user.avatar || "/placeholder.svg"}
                      alt={notification.user.displayName}
                    />
                    <AvatarFallback>
                      {notification.user.displayName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      {getNotificationIcon(notification.type)}
                      <span className="text-sm">
                        <span className="font-semibold">
                          {notification.user.username}
                        </span>{" "}
                        {getNotificationText(notification)}
                      </span>
                      {!notification.isRead && (
                        <div className="h-2 w-2 bg-primary rounded-full" />
                      )}
                    </div>
                    {notification.content && (
                      <p className="text-sm text-muted-foreground">
                        {notification.content}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(notification.createdAt, {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
